import React, { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import CommandCenter from './pages/CommandCenter';
import MaterialAck from './pages/MaterialAck';
import Onboarding from './pages/Onboarding';
import Servers from './pages/Servers';
import OnboardingStaff from './pages/OnboardingStaff';
import AckSign, { AckHrSign } from './pages/AckSign';
import Tasks from './pages/Tasks';
import Assets from './pages/Assets';
import Configs from './pages/Configs';
import Scrap from './pages/Scrap';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Permissions from './pages/Permissions';
import Master from './pages/Master';
import Settings from './pages/Settings';
import HealthChecks from './pages/HealthChecks';
import Incidents from './pages/Incidents';
import Routines from './pages/Routines';
import Rooms from './pages/Rooms';
import SystemLogs from './pages/SystemLogs';
import UserActivity from './pages/UserActivity';
import ApprovalCenter from './pages/ApprovalCenter';
import Announcements from './pages/Announcements';
import BackupManagement from './pages/BackupManagement';
import Documents from './pages/Documents';
import AiLogs from './pages/AiLogs';
import MailCenter from './pages/MailCenter';
import Feedback from './pages/Feedback';
import RequesterPortal from './pages/RequesterPortal';
import CoordinatorContent from './pages/CoordinatorDashboard';
import CeoDashboard from './pages/CeoDashboard';
import MeetingPoints from './pages/MeetingPoints';
import Containers from './pages/Containers';
import Analytics from './pages/Analytics';
import Performance from './pages/Performance';
import VendorManagement from './pages/VendorManagement';
import Problems from './pages/Problems';
import Recycle from './pages/Recycle';
import Policies from './pages/Policies';
import MyPolicies from './pages/MyPolicies';
import Calendar from './pages/Calendar';
import Training from './pages/Training';
import FeeUpload from './pages/FeeUpload';
import FeeDashboard from './pages/FeeDashboard';
import Surveillance from './pages/Surveillance';
import RemoteSupport from './pages/RemoteSupport';
import Sidebar from './components/Sidebar';
import QuickLinks from './components/QuickLinks';
import NotificationBell from './components/NotificationBell';
import UniversalSearch from './components/UniversalSearch';

function AppInner() {
  const { user, logout, loading } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [pageArg, setPageArg] = useState(null);   // optional argument passed to a page (e.g. container id to open)
  const [navOpen, setNavOpen] = useState(false);  // mobile: is the menu drawer showing?

  // Landing page. This runs as an effect rather than as the initial state
  // because `user` is still null on the first render — the session is being
  // restored — so a role test at useState() time always loses.
  // Admin / IT Head land on the Command Center: it is the screen that says what
  // needs them today. It fires once, so it never yanks anyone off a page they
  // navigated to themselves.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || !user) return;
    landed.current = true;
    if (['admin', 'it_head'].includes(user.role)) setPage('command');
  }, [user]);

  // Esc closes the topmost open modal (backdrop-click no longer closes, so this
  // is the keyboard escape hatch). Clicks the last visible modal's × button.
  useEffect(() => {
    const onKey = e => {
      if (e.key !== 'Escape') return;
      const closers = document.querySelectorAll('.modal-overlay .modal-close');
      if (closers.length) closers[closers.length - 1].click();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--text2)' }}>
      Loading…
    </div>
  );

  if (!user) return <Login />;
  if (user.must_change_password) return <ChangePassword />;

  // Teaching staff — self-service portal
  // Every non-IT portal gets "My Policies" as a shared nav item — policy
  // acknowledgement applies to all staff, not just the IT team.
  const POL_NAV = ['mypolicies', '📋 My Policies'];
  // ctx carries the acknowledgement the notification bell asked us to open.
  const withPolicies = (pg, ctx, render) => pg === 'mypolicies'
    ? <MyPolicies initialAck={ctx?.policyAck} clearArg={ctx?.clearPolicyAck} />
    : render();

  if (user.role === 'requester')
    return <RequesterPortal extraNav={[POL_NAV]}
      renderExtra={(pg, _go, ctx) => withPolicies(pg, ctx, () => null)} />;
  // Coordinator / HOS / Management get the same self-service portal PLUS their
  // role dashboard as the first nav item.
  if (user.role === 'coordinator')
    return <RequesterPortal portalTitle="Coordinator Portal" defaultPage="dept"
      extraNav={[['dept','🏢 Department'], POL_NAV]}
      renderExtra={(pg, _go, ctx) => withPolicies(pg, ctx, () => <CoordinatorContent />)} />;
  // HOS = campus-level leader. Their Campus Overview IS the all-department
  // dashboard (scoped to their campus by the API); IT detail lives behind the
  // IT tile rather than as its own menu item.
  if (user.role === 'principal')
    return <RequesterPortal portalTitle="HOS Console" defaultPage="overview"
      extraNav={[['overview','🏛 Campus Overview'], POL_NAV]}
      renderExtra={(pg, go, ctx) => withPolicies(pg, ctx, () => <CeoDashboard go={go} />)} />;
  // Finance: the staff portal plus the two fee screens.
  if (user.role === 'finance')
    return <RequesterPortal portalTitle="Finance Portal" defaultPage="feedash"
      extraNav={[['feedash','💰 Fee Dashboard'],['feeupload','⬆ Fee Report Upload'], POL_NAV]}
      renderExtra={(pg, _go, ctx) => withPolicies(pg, ctx, () => pg === 'feeupload' ? <FeeUpload /> : <FeeDashboard />)} />;
  // Management: all-campus operations dashboard. The IT HQ view is reached by
  // clicking the IT tile, so it doesn't need its own menu item.
  if (user.role === 'management')
    return <RequesterPortal portalTitle="Management Console" defaultPage="ceo"
      extraNav={[['ceo','📈 Dashboard'], POL_NAV]}
      renderExtra={(pg, go, ctx) => withPolicies(pg, ctx, () => <CeoDashboard go={go} />)} />;

  // Navigation that can carry an argument (e.g. open a specific container).
  const go = (p, arg) => { setPage(p); setPageArg(arg ?? null); };
  const pages = { dashboard:<Dashboard go={setPage} />,
    command:<CommandCenter go={go} />,
    materialack:<MaterialAck />,
    onboarding:<Onboarding openId={pageArg} onOpened={() => setPageArg(null)} />,
    studentonboarding:<Onboarding kind="student" />,
    servers:<Servers />,
    tasks:<Tasks go={go} arg={pageArg} clearArg={() => setPageArg(null)} />,
    // Projects and the two meeting pages are the SAME component in the same
    // slot, so React reuses one instance and keeps its state across the switch —
    // which left a meeting still open, and editable, after clicking Projects.
    // The key makes each page its own instance.
    projects:<Containers key="projects" mode="project" title="Projects" openId={pageArg} clearArg={() => setPageArg(null)} />,
    // One Meetings screen. The kind used to be two pages with the code baked
    // in; it is now master data, chosen when a meeting is created and
    // filterable in the list.
    meetings:<Containers key="meetings" mode="meeting" title="Meetings" openId={pageArg} clearArg={() => setPageArg(null)} />,
    // The old page keys still resolve, so a bookmark or a notification link
    // from before this change lands somewhere sensible instead of a blank page.
    meet_head:<Containers key="meetings" mode="meeting" title="Meetings" openId={pageArg} clearArg={() => setPageArg(null)} />,
    meet_lead:<Containers key="meetings" mode="meeting" title="Meetings" openId={pageArg} clearArg={() => setPageArg(null)} />,
    meetingpoints:<MeetingPoints go={setPage} />, analytics:<Analytics />, performance:<Performance />, ceo:<CeoDashboard go={go} />, vendormgmt:<VendorManagement />, problems:<Problems />, recycle:<Recycle />, surveillance:<Surveillance />, remote:<RemoteSupport />, routines:<Routines />, healthchecks:<HealthChecks />, incidents:<Incidents />, assets:<Assets />, rooms:<Rooms />, configs:<Configs />, scrap:<Scrap />, policies:<Policies />,
    mypolicies:<MyPolicies initialAck={pageArg} clearArg={() => setPageArg(null)} />,
    calendar:<Calendar go={go} />,
    training:<Training openId={pageArg} clearArg={() => setPageArg(null)} />, feeupload:<FeeUpload />, feedash:<FeeDashboard />, users:<Users />, roles:<Roles />, permissions:<Permissions />, master:<Master />, approvals:<ApprovalCenter />, announcements:<Announcements />, documents:<Documents />, ailogs:<AiLogs />, mail:<MailCenter />, feedback:<Feedback />, logs:<SystemLogs />, activity:<UserActivity />, backup:<BackupManagement />, settings:<Settings /> };

  // On a phone the 220px sidebar left almost no room, so the page overflowed
  // sideways and the browser zoomed out — which is why the text looked tiny.
  // Below 820px it collapses behind the ☰ button instead.
  const goMobile = (p, arg) => { go(p, arg); setNavOpen(false); };

  return (
    <div className="app-shell" style={{ display:'flex', height:'100vh', overflow:'hidden' }}>
      <Sidebar page={page} setPage={p => { setPage(p); setNavOpen(false); }} collapsed={!navOpen} />
      <main style={{ flex:1, minWidth:0, overflowY:'auto', display:'flex', flexDirection:'column' }}>
        {/* Top bar — school branding with quick links underneath (top-right) */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
          padding:'10px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg2)',
          position:'sticky', top:0, zIndex:50, gap:10 }}>
          <button className="nav-toggle btn btn-secondary btn-sm" aria-label="Menu"
            aria-expanded={navOpen} onClick={() => setNavOpen(o => !o)}>☰</button>
          {/* One search box for the whole portal, always to hand. */}
          <div className="top-search" style={{ flex:1, maxWidth:560, minWidth:0 }}>
            <UniversalSearch go={goMobile} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, marginLeft:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              {/* Training lives inside the Tasks page, so route it there with a marker */}
              <NotificationBell onOpen={n => goMobile(n.link_page || 'dashboard', n.link_arg)} />
              <img src={`${process.env.PUBLIC_URL || ''}/school-logo.png`} alt="TIPS Globeducate"
                className="brand-logo" style={{ height:44, width:'auto', objectFit:'contain' }} />
            </div>
            {page === 'dashboard' && <QuickLinks />}
          </div>
        </div>
        <div className="page-pad" style={{ padding:'24px' }}>
          {pages[page] || <Dashboard />}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  // /ack/<token> is the employee's one-time acknowledgement page. It is
  // deliberately OUTSIDE AuthProvider: the person opening it has no portal
  // account, and anything inside the provider would try to restore a session
  // and send them to the login screen.
  // /ack/hr/<token> is checked FIRST: the employee pattern would otherwise have
  // to be careful not to swallow it.
  const hr = window.location.pathname.match(/^\/ack\/hr\/([A-Za-z0-9_-]{20,})$/);
  if (hr) return <AckHrSign token={hr[1]} />;
  const m = window.location.pathname.match(/^\/ack\/([A-Za-z0-9_-]{20,})$/);
  if (m) return <AckSign token={m[1]} />;
  // /onboarding/<token> is the new joiner's induction page — also outside
  // AuthProvider, because on day one they have no portal account.
  const onb = window.location.pathname.match(/^\/onboarding\/([A-Za-z0-9_-]{20,})$/);
  if (onb) return <OnboardingStaff token={onb[1]} />;

  return <AuthProvider><AppInner /></AuthProvider>;
}
